import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { shiftApi, venueApi } from "../../../api/resources";
import type { VenueAdmin } from "../../../api/types";
import { PageHeader } from "../../../components/ComingSoon";
import { Modal, SelectFilter } from "../../../components/Overlay";
import { Button, DemoBadge, StatusPill, TextInput } from "../../../components/ui";
import { DataTable, LoadState, useLoad } from "../shared";

const STATUS: [string, string][] = [["ACTIVE", "Active"], ["ARCHIVED", "Archived"], ["ALL", "All"]];

export default function AdminVenues() {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [reload, setReload] = useState(0);
  const [archiving, setArchiving] = useState<VenueAdmin | null>(null);
  const [impact, setImpact] = useState<number | null>(null);
  useEffect(() => {
    setImpact(null);
    if (archiving) shiftApi.venueImpact(archiving.id).then((r) => setImpact(r.future_assignments)).catch(() => setImpact(null));
  }, [archiving]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(t);
  }, [q]);
  const loader = useCallback(() => venueApi.list(debounced || undefined, status), [debounced, status, reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad<VenueAdmin[]>(loader);

  const act = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      toast.success(msg);
      setArchiving(null);
      setReload((r) => r + 1);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <section data-testid="admin-venues-page" className="animate-rise">
      <PageHeader title="Venues">
        <Link to="/admin/venues/new" data-testid="venue-create-link">
          <Button tabIndex={-1}><Plus className="h-4 w-4" /> New venue</Button>
        </Link>
      </PageHeader>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-zinc-500" />
          <TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or city" className="pl-9" data-testid="venues-search-input" />
        </div>
        <SelectFilter value={status} onChange={setStatus} options={STATUS} testId="venues-status-filter" label="Venue status" />
      </div>
      <LoadState error={error} loading={!data} testId="venues-load-error" />
      {data && (
        <DataTable testId="venues-table" headers={["Venue", "City", "Timezone", "Geofence", "Status", ""]} empty={data.length === 0}>
          {data.map((v) => (
            <tr key={v.id} data-testid={`venue-row-${v.id}`} className="hover:bg-white/[0.03]">
              <td className="px-5 py-4">
                <Link to={`/admin/venues/${v.id}`} data-testid={`venue-link-${v.id}`} className="mr-2 text-white hover:text-signal">{v.name}</Link>
                <DemoBadge show={v.is_demo} testId={`venue-demo-badge-${v.id}`} />
              </td>
              <td className="px-5 py-4 text-zinc-300">{[v.city, v.state].filter(Boolean).join(", ") || "—"}</td>
              <td className="px-5 py-4 font-mono text-xs text-zinc-300">{v.timezone}</td>
              <td className="px-5 py-4 text-zinc-300" data-testid={`venue-radius-${v.id}`}>{v.geofence_radius_feet.toLocaleString()} ft</td>
              <td className="px-5 py-4"><StatusPill status={v.status} testId={`venue-status-${v.id}`} /></td>
              <td className="px-5 py-4 text-right">
                {v.status === "ACTIVE" ? (
                  <Button variant="ghost" className="h-8 px-3 text-[11px] hover:text-signal" onClick={() => setArchiving(v)} data-testid={`venue-archive-${v.id}`}>Archive</Button>
                ) : (
                  <Button variant="secondary" className="h-8 px-3 text-[11px]" onClick={() => act(() => venueApi.restore(v.id), "Venue restored")} data-testid={`venue-restore-${v.id}`}>Restore</Button>
                )}
              </td>
            </tr>
          ))}
        </DataTable>
      )}
      <Modal open={!!archiving} onClose={() => setArchiving(null)} title="Archive venue?" testId="venue-archive-modal">
        <p className="text-sm text-zinc-300"><span className="text-white">{archiving?.name}</span> will be hidden from the default list. You can restore it later.</p>
        {impact !== null && impact > 0 && (
          <p data-testid="venue-archive-impact-warning" className="mt-4 rounded-sm border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
            {impact} upcoming shift assignment{impact === 1 ? "" : "s"} at this venue will be flagged for attention. They are not cancelled automatically.
          </p>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setArchiving(null)} data-testid="venue-archive-cancel-button">Cancel</Button>
          <Button onClick={() => archiving && act(() => venueApi.archive(archiving.id), "Venue archived")} data-testid="venue-archive-confirm-button">Archive</Button>
        </div>
      </Modal>
    </section>
  );
}
